import type { PaymentConfig, SaveVendorFeesPayload, VendorFee } from 'src/interfaces/payment'
import { toNumber } from 'src/utils/paymentConfig'
import { toCentsInput, toRateInput } from '../paymentInputs'
import type { VendorFeeFormRow, VendorFeesFormValues } from './VendorFeesForm.interface'

/** The caller supplies the id so this stays pure — see `VendorFeesFormContent`'s counter. */
export function buildEmptyRow(id: string): VendorFeeFormRow {
  return {
    id,
    fee: '',
    rate: '',
    feeCapCents: '',
    type: '',
    title: '',
    applicableDestinations: [],
    contractorType: [],
  }
}

export function toFormValues(config: PaymentConfig): VendorFeesFormValues {
  return {
    rows: config.invoiceBreakdowns.map((row, index) => {
      return {
        // Positional, and deliberately so: a jsonb row carries no server id, so its position in
        // the persisted array is the only identity it has. Deterministic keeps the mapper pure.
        id: `row-${index}`,
        fee: toCentsInput(row.fee),
        rate: toRateInput(row.rate),
        feeCapCents: toCentsInput(row.feeCapCents),
        type: row.type,
        title: row.title,
        applicableDestinations: row.applicableDestinations,
        contractorType: row.contractorType,
      }
    }),
  }
}

/**
 * Both array keys are always emitted. `@IsEnum(…, { each: true })` on the DTO carries no
 * `@IsOptional()`, so an omitted key fails validation — but an empty array checks zero items and
 * passes, which is the state most live rows are already in.
 *
 * The `(fee > 0 || rate > 0) && title && type` filter is Retool's, kept as a backstop. In Retool
 * it existed to discard the blank row that table always rendered; here the Add button means
 * there is no blank row and the validator rejects anything this would drop, so it should never
 * remove a row the admin can see.
 */
export function toSavePayload(
  partnerId: string,
  values: VendorFeesFormValues,
): SaveVendorFeesPayload {
  const invoiceBreakdowns = values.rows.reduce<VendorFee[]>((rows, row) => {
    const fee = toNumber(row.fee)
    const rate = toNumber(row.rate)

    if ((fee <= 0 && rate <= 0) || row.title.trim() === '' || row.type === '') {
      return rows
    }

    rows.push({
      title: row.title.trim(),
      type: row.type,
      rate,
      fee,
      feeCapCents: toNumber(row.feeCapCents),
      applicableDestinations: row.applicableDestinations,
      contractorType: row.contractorType,
    })

    return rows
  }, [])

  return { partnerId, invoiceBreakdowns }
}
